import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router';
import type { ApiError, ScannerContext, SessionResponse } from './api';
import { endpoints } from './api';
import { FullPageMessage } from './ui';

export function useSession() {
  return useQuery<SessionResponse, ApiError>({ queryKey: ['session'], queryFn: endpoints.session, retry: false, staleTime: 5 * 60_000 });
}

export function useScannerContext() {
  return useQuery<ScannerContext, ApiError>({ queryKey: ['context'], queryFn: endpoints.context, retry: false, staleTime: 60_000 });
}

/** Signed in, or offline with a session we saw before: the gate must keep working when the network drops. */
export function RequireSession({ children }: { children: ReactNode }) {
  const session = useSession();
  const [known, setKnown] = useState(() => {
    try {
      return localStorage.getItem('scanner.session') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (session.data) {
      setKnown(true);
      try {
        localStorage.setItem('scanner.session', '1');
      } catch {
        // storage blocked
      }
    }
    if (session.error?.isUnauthenticated) {
      setKnown(false);
      try {
        localStorage.removeItem('scanner.session');
      } catch {
        // storage blocked
      }
    }
  }, [session.data, session.error]);
  if (session.isPending && !known) return <FullPageMessage title="Checking your session…" />;
  if (session.error?.isUnauthenticated) return <Navigate to="/login" replace />;
  if (session.error && !session.error.isOffline && !known) return <FullPageMessage title="Could not load your session" detail={session.error.message} tone="bad" />;
  return <>{children}</>;
}

// --- The gate selection, kept on the device ---------------------------------------------------

export interface GateSelection {
  performance_ids: string[];
  gate_id: string | null;
  gate_name: string | null;
  input: 'CAMERA' | 'HID_SCANNER';
}

const SelectionContext = createContext<{ selection: GateSelection | null; setSelection: (s: GateSelection | null) => void } | null>(null);

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [selection, setSelectionState] = useState<GateSelection | null>(() => {
    try {
      const raw = localStorage.getItem('scanner.selection');
      return raw ? (JSON.parse(raw) as GateSelection) : null;
    } catch {
      return null;
    }
  });
  const setSelection = (s: GateSelection | null) => {
    setSelectionState(s);
    try {
      if (s) localStorage.setItem('scanner.selection', JSON.stringify(s));
      else localStorage.removeItem('scanner.selection');
    } catch {
      // storage blocked
    }
  };
  return <SelectionContext.Provider value={{ selection, setSelection }}>{children}</SelectionContext.Provider>;
}

export function useSelection() {
  const value = useContext(SelectionContext);
  if (!value) throw new Error('useSelection outside <SelectionProvider>');
  return value;
}

/** The device clock minus the server clock, measured whenever the context loads; sent with offline scans. */
export function clockOffsetMs(): number | null {
  try {
    const raw = localStorage.getItem('scanner.clock_offset_ms');
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
}

export function recordClockOffset(serverTime: string): void {
  try {
    localStorage.setItem('scanner.clock_offset_ms', String(Date.now() - new Date(serverTime).getTime()));
  } catch {
    // storage blocked
  }
}
